/**
 * The cart drawer.
 *
 * * **On a shop page** it lists that shop's lines (in its currency) and sends you
 *   to its checkout.
 * * **In the community** (the Shop page, the feed…) there is no “current shop”,
 *   so the lines are **grouped per shop** — each group has its own subtotal and
 *   its own checkout button, because every shop is paid separately. `CartContext`
 *   keeps `shop_id` (and the shop name / username / currency) on every line.
 */
import React from 'react';
import { FiMinus, FiPlus, FiShoppingBag, FiShoppingCart, FiTrash2, FiX } from 'react-icons/fi';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { useShop } from '../contexts/ShopContext';
import { useLanguage } from '../i18n';
import { fullUrl } from '../api';
import { BLUE } from './community/brand';

export default function CartSidebar() {
  const { items, open, setOpen, removeItem, updateQty } = useCart();
  const { shop } = useShop();
  const { t } = useLanguage();
  const navigate = useNavigate();

  if (!open) return null;

  // The lines to show: this shop's on a shop page, every shop's in the community.
  const rows = items
    .map((item, index) => ({ item, index }))
    .filter(({ item }) => !shop?.id || item.shop_id === shop.id);
  const others = items.length - rows.length;

  // Group them per shop so each one can be paid on its own.
  const groups = [];
  rows.forEach((row) => {
    const key = String(row.item.shop_id ?? '');
    let group = groups.find((g) => g.key === key);
    if (!group) {
      group = {
        key,
        username: row.item.shop_username || '',
        name: row.item.shop_name || '',
        currency: row.item.currency || shop?.currency || 'USD',
        rows: [],
      };
      groups.push(group);
    }
    if (!group.username && row.item.shop_username) group.username = row.item.shop_username;
    if (!group.name && row.item.shop_name) group.name = row.item.shop_name;
    group.rows.push(row);
  });
  groups.forEach((g) => {
    g.count = g.rows.reduce((sum, r) => sum + r.item.quantity, 0);
    g.subtotal = Math.round(g.rows.reduce((sum, r) => sum + r.item.price * r.item.quantity, 0) * 100) / 100;
  });

  const count = rows.reduce((sum, r) => sum + r.item.quantity, 0);
  const onlyShop = !!shop?.id;                      // on a shop page → one group

  const line = ({ item, index }) => (
    <div key={index} className="flex gap-3 bg-gray-50 dark:bg-gray-700 rounded-xl p-3">
      {item.image && (
        <img src={fullUrl(item.image)} alt="" className="w-16 h-16 rounded-lg object-cover" />
      )}
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-sm truncate">{item.name}</p>
        {Object.keys(item.variations || {}).length > 0 && (
          <p className="text-xs text-gray-500">
            {Object.entries(item.variations).map(([k, v]) => `${k}: ${v}`).join(' · ')}
          </p>
        )}
        <div className="flex items-center justify-between mt-2">
          <div className="flex items-center gap-2 bg-white dark:bg-gray-700 rounded-lg border px-1 py-0.5">
            <button onClick={() => updateQty(index, item.quantity - 1)} className="p-1 hover:bg-gray-100 rounded">
              <FiMinus className="w-3 h-3" />
            </button>
            <span className="text-sm font-semibold w-6 text-center">{item.quantity}</span>
            <button onClick={() => updateQty(index, item.quantity + 1)} className="p-1 hover:bg-gray-100 rounded">
              <FiPlus className="w-3 h-3" />
            </button>
          </div>
          <span className="font-bold text-sm">{(item.price * item.quantity).toFixed(2)}</span>
        </div>
      </div>
      <button onClick={() => removeItem(index)} className="text-gray-400 hover:text-red-500 self-start p-1">
        <FiTrash2 className="w-4 h-4" />
      </button>
    </div>
  );

  return (
    <div className="fixed inset-0 z-[1200]">
      <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
      <div className="absolute right-0 top-0 h-full w-full max-w-md bg-white dark:bg-gray-800 shadow-2xl flex flex-col">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-bold flex items-center gap-2">
            <FiShoppingCart /> {t('yourCart')} ({count})
          </h2>
          <button onClick={() => setOpen(false)} className="p-2 rounded-lg hover:bg-gray-100"><FiX /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {rows.length === 0 ? (
            <div className="text-center py-16 text-gray-400">
              <FiShoppingCart className="w-14 h-14 mx-auto mb-3 text-gray-200" />
              <p>{t('cartEmpty')}</p>
              <Link to="/shop" onClick={() => setOpen(false)}
                className="mt-3 inline-block text-sm font-bold" style={{ color: BLUE }}>
                {t('allProducts')} →
              </Link>
            </div>
          ) : onlyShop ? (
            <div className="space-y-4">{rows.map(line)}</div>
          ) : (
            // Community: one block per shop (each shop is paid separately)
            <div className="space-y-5">
              {groups.map((group) => (
                <div key={group.key} className="space-y-2">
                  <div className="flex items-center gap-2">
                    <FiShoppingBag className="w-3.5 h-3.5 text-gray-400" />
                    <span className="text-xs font-extrabold uppercase tracking-wide text-gray-500 truncate">
                      {group.name || group.username || t('shopNav')}
                    </span>
                  </div>
                  <div className="space-y-3">{group.rows.map(line)}</div>
                  <div className="flex items-center justify-between gap-2 pt-1">
                    <span className="text-sm text-gray-500">
                      {group.subtotal.toFixed(2)} {group.currency}
                    </span>
                    {group.username ? (
                      <button
                        onClick={() => { setOpen(false); navigate(`/${group.username}/checkout`); }}
                        className="px-3 py-2 rounded-xl text-white text-xs font-bold"
                        style={{ background: BLUE }}
                      >
                        {t('proceedCheckout')}
                      </button>
                    ) : (
                      <Link to="/shop" onClick={() => setOpen(false)}
                        className="text-xs font-bold" style={{ color: BLUE }}>
                        {t('allProducts')} →
                      </Link>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {rows.length > 0 && onlyShop && (
          <div className="border-t p-4 space-y-3">
            {others > 0 && (
              <p className="text-xs rounded-lg px-3 py-2 bg-amber-50 text-amber-800 border border-amber-200">
                {others} {t('productsCount')} ·{' '}
                <Link to="/shop" onClick={() => setOpen(false)} className="font-bold underline">
                  {t('allProducts')}
                </Link>
              </p>
            )}
            <div className="flex justify-between items-center">
              <span className="text-gray-600">{t('subtotal')}</span>
              <span className="text-xl font-bold">
                {groups[0]?.subtotal.toFixed(2)} {groups[0]?.currency}
              </span>
            </div>
            <button
              onClick={() => { setOpen(false); navigate(`/${shop.username}/checkout`); }}
              className="w-full btn-primary py-3 rounded-xl font-semibold"
            >
              {t('proceedCheckout')}
            </button>
            <button
              onClick={() => setOpen(false)}
              className="w-full py-2 rounded-xl text-gray-500 hover:bg-gray-100"
            >
              {t('continueShopping')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
